/**
 * ประวัติการนำเข้า — the filter vocabulary, shared by the LIST and the run detail's prev/next.
 *
 * ⚠️ THIS FILE EXISTS FOR ONE REASON. Item 6's neighbour set must be the identical predicate
 * to the list, or prev/next walks into runs that are not on the operator's screen — and that
 * bug is silent. One shared `filterRuns` makes it true by construction rather than by
 * discipline. Do not reimplement either half.
 *
 * ⚠️ EVERY PARAM IS NAMESPACED `ci_`. The ledger tab already owns `page`, `perPage` and
 * `payer`, and the obvious next change — URL-ifying the ledger's pager — would silently
 * collide on live money filters now that the three pages share one URL.
 *
 * ⚠️ `payer` GOES ON THE WIRE; month / source / status are filtered CLIENT-SIDE.
 * Not a shortcut — `source` is not declared as an input on Xano api 629, and Xano SILENTLY
 * IGNORES undeclared query inputs, so a server-side source filter would ship looking wired
 * up and quietly return everything. (That exact failure already happened on this screen:
 * the payer filter filed AIA under Generali for a day.) Numbered pages also need a total,
 * and 629 returns a hand-built {runs:[…]}, not Xano's paging envelope. Keeping payer on the
 * wire matters the other way: `?payer=AXA&per_page=500` gives the newest 500 AXA runs,
 * whereas filtering 500 mixed runs in the browser gives only the AXA ones inside the newest
 * 500 overall — and Prudential sends one file per branch, so the mixed list fills far faster.
 */

import { runPhase } from './claimLink.js'

/** `source` values api 629 can return. The bare two-arm ternary this replaces rendered ANY
 *  unknown source as 'ทำซ้ำ'. */
export const SOURCES = [
  { v: 'manual_upload', label: 'อัปโหลดเอง' },
  { v: 'email_robot', label: 'อีเมลอัตโนมัติ' },
  { v: 'replay', label: 'ทำซ้ำ' },
]
const SOURCE_LABEL = new Map(SOURCES.map((s) => [s.v, s.label]))
export const sourceLabel = (v) => SOURCE_LABEL.get(v) || (v ? `อื่น ๆ (${v})` : '—')

/**
 * The filter vocabulary is now the DERIVED phase, not `run.status`.
 *
 * ⚠️ IT HAD TO CHANGE WITH THE CHIP, OR THE TWO WOULD DISAGREE. `run.status` answers
 * "did somebody press the button", which is not the question anyone opening this screen is
 * asking. Filtering on `committed` returned 57 runs, 47 of which still held unaccounted
 * money — so the filter that looked like "show me the finished ones" was the filter that
 * hid the backlog. The phase comes from claimLink.js and is computed from the counters.
 *
 * `staged` / `committed` / `reverted` collapse into ยังไม่ครบ or ครบแล้ว on the only thing
 * that matters: is there money in this file nobody has accounted for.
 */
export const RUN_STATUSES = [
  { v: 'open', label: 'ยังไม่ครบ' },
  { v: 'done', label: 'ครบแล้ว' },
  { v: 'parse_failed', label: 'อ่านไฟล์ไม่ได้' },
  { v: 'rejected', label: 'ไม่ผ่านการตรวจ' },
]

/**
 * ⚠️ A BOOKMARK OR A PERSISTED FILTER MAY STILL HOLD AN OLD `run.status` TOKEN, and a
 * status nothing matches renders as an EMPTY TABLE with no explanation — the one outcome
 * this screen's own comments call out as unacceptable ("a failed run must render as a row,
 * never as an absence"). Old tokens degrade to the phase that means the same thing.
 */
const LEGACY_STATUS = new Map([
  ['staged', 'open'],
  ['committed', 'done'],
  ['partially_committed', 'open'],
  ['reverted', 'open'],
  ['partially_reverted', 'open'],
])
export const normaliseStatusFilter = (v) => (v ? LEGACY_STATUS.get(v) || v : '')

/**
 * 'YYYY-MM' for a run, in BANGKOK time — a fixed +7, never the browser's zone.
 * A run imported at 00:30 Bangkok is a UTC-yesterday timestamp; using the browser's offset
 * would file it under the previous month for anyone whose machine is not on Asia/Bangkok,
 * and it would then be unfindable under the month its own date column displays.
 */
export function runMonthKey(ms) {
  const n = Number(ms)
  if (!Number.isFinite(n) || !n) return ''
  const d = new Date(n + 7 * 3600 * 1000)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

/** 'ก.ย. 2569' — Buddhist year, matching every other date on these screens. */
export function monthLabel(key) {
  const [y, m] = String(key || '').split('-').map(Number)
  if (!y || !m) return key || ''
  return `${TH_MONTHS[m - 1]} ${y + 543}`
}

/** Distinct months present in the loaded runs, newest first. */
export function monthOptions(runs) {
  const seen = new Set()
  for (const r of runs || []) {
    const k = runMonthKey(r?.created_at)
    if (k) seen.add(k)
  }
  return [...seen].sort().reverse()
}

export function readRunFilters(params) {
  const g = (k) => params?.get?.(k) || ''
  return {
    payer: g('ci_payer'),
    source: g('ci_source'),
    status: g('ci_status'),
    month: g('ci_month'),
    q: g('ci_q'),
    sort: SORTS.has(g('ci_sort')) ? g('ci_sort') : '',
    page: Math.max(1, Number(g('ci_page')) || 1),
  }
}

/** Serialise filters back onto a URL. `extra` overrides, e.g. { ci_page: 3 }. */
export function runQueryString(f, extra = {}) {
  const p = new URLSearchParams()
  const put = (k, v) => { if (v != null && v !== '' && v !== 1) p.set(k, String(v)) }
  put('ci_payer', f?.payer)
  put('ci_source', f?.source)
  put('ci_status', f?.status)
  put('ci_month', f?.month)
  put('ci_q', f?.q)
  put('ci_sort', f?.sort)
  put('ci_page', f?.page)
  for (const [k, v] of Object.entries(extra)) {
    if (v == null || v === '' || v === 1) p.delete(k)
    else p.set(k, String(v))
  }
  return p.toString()
}

/**
 * วันที่ส่ง — when the insurer SENT the file, as epoch ms, or null when nothing says.
 *
 * ⚠️ ONLY THE ROBOT KNOWS THE EMAIL'S DATE. A manual upload is a file dropped on the page;
 * nothing about it says which email it came out of, so `email_received_at` is 0. For those
 * the nearest honest stand-in is the date the file carries itself — AIA's letter date on
 * page 1, Generali's / Prudential's payment date. An operator-typed bank date is NOT used:
 * it says when money landed, not when anything was sent.
 * `kind` is 'email' | 'file' so the screen can say which one it is showing.
 */
export function runSentAt(r) {
  const em = Number(r?.email_received_at)
  if (em > 0) return { ms: em, kind: 'email' }
  const src = String(r?.transfer_date_source || '')
  const [y, m, d] = String(r?.transfer_date || '').slice(0, 10).split('-').map(Number)
  if (y && m && d && src && src !== 'absent' && src !== 'operator_entered') {
    // Bangkok midnight of that calendar day, so it sorts sensibly against email timestamps.
    return { ms: Date.UTC(y, m - 1, d) - 7 * 3600 * 1000, kind: 'file' }
  }
  return null
}

/**
 * Column sorts. '' = the server's order (newest import first), which is also what every
 * old bookmark means.
 * ⚠️ Applied INSIDE filterRuns, not by the list page, for the same reason the filters are:
 * prev/next on the run page walks this array, and an order that exists only on the list
 * would send → to a run that is not the next row on the operator's screen.
 * Runs with no วันที่ส่ง sink to the BOTTOM in both directions — "unknown" is not "oldest".
 */
const SORTS = new Set(['imported_asc', 'imported_desc', 'sent_asc', 'sent_desc'])
export function compareRuns(sort) {
  const byId = (a, b) => (Number(b?.id) || 0) - (Number(a?.id) || 0)
  if (!SORTS.has(sort)) return byId
  const [col, dir] = sort.split('_')
  const sign = dir === 'asc' ? 1 : -1
  const key = col === 'sent'
    ? (r) => runSentAt(r)?.ms ?? null
    : (r) => Number(r?.created_at) || null
  return (a, b) => {
    const ka = key(a), kb = key(b)
    if (ka == null && kb == null) return byId(a, b)
    if (ka == null) return 1
    if (kb == null) return -1
    return (ka - kb) * sign || byId(a, b)
  }
}

/**
 * Patient search — HN, ชื่อ or นามสกุล, matched against every row of the run.
 *
 * ⚠️ READS `progress_rows`, which runs.js attaches from api 661 (hn / crm_name /
 * file_patient_name). When that call fails the rows are absent and NOTHING matches — the
 * list shows its own warning for that case; a search must never fall back to "everything".
 *
 * ⚠️ HN IS COMPARED DIGITS-ONLY. Staff type `6548872`, `654-8872` and `654 8872`
 * interchangeably, and filenames already drop the dash (knowledge/insurance/README.md §4).
 * Names match on BOTH the CRM's name and the file's, because an unmatched row has only the
 * file's — and those are exactly the rows someone goes looking for.
 * Every whitespace-separated word must hit, so `สมหญิง ทดสอบ` finds the full name.
 */
const digits = (v) => String(v || '').replace(/\D/g, '')
const squash = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim()
export function runMatchesPatient(run, q) {
  const words = squash(q).split(' ').filter(Boolean)
  if (!words.length) return true
  const rows = Array.isArray(run?.progress_rows) ? run.progress_rows : []
  return rows.some((r) => {
    const names = `${squash(r?.crm_name)} ${squash(r?.file_patient_name)}`
    const hn = digits(r?.hn)
    return words.every((w) => {
      const d = digits(w)
      // A word that is all digits/dashes is an HN fragment; anything else is a name.
      if (d && d.length === w.replace(/[\s-]/g, '').length) return hn.includes(d)
      return names.includes(w)
    })
  })
}

/**
 * ⚠️ THE ONE PREDICATE. Used by the list AND by prev/next's neighbour resolver.
 * `payer` is applied here too even though it also goes on the wire — the detail page's
 * resolver may hold a broader array, and a filter that is right in one place and absent in
 * the other is the silent mismatch this module exists to prevent.
 */
export function filterRuns(runs, f) {
  const list = Array.isArray(runs) ? runs : []
  if (!f) return list
  const out = list.filter((r) => {
    if (f.payer && String(r?.payer || '') !== f.payer) return false
    if (f.source && String(r?.source || '') !== f.source) return false
    /* ⚠️ Compared against the DERIVED phase, never `r.status` — see RUN_STATUSES. A run
       that committed 1 of 11 rows is `committed` and is NOT done. */
    if (f.status && runPhase(r) !== normaliseStatusFilter(f.status)) return false
    if (f.month && runMonthKey(r?.created_at) !== f.month) return false
    if (f.q && !runMatchesPatient(r, f.q)) return false
    return true
  })
  return f.sort ? out.sort(compareRuns(f.sort)) : out
}
